import { useEffect, useRef, useState, type ReactNode, type KeyboardEvent } from 'react'

interface Props<T> {
  value: string
  onChange: (value: string) => void
  onPick: (item: T) => void
  search: (query: string) => Promise<T[]>
  render: (item: T) => ReactNode
  keyOf: (item: T) => string
  placeholder?: string
  disabled?: boolean
  /** Shown when the term matches nothing, e.g. "not in the drug list". */
  emptyNote?: string
}

/**
 * Type-ahead over a master list. Free text is always kept: a drug that is not
 * in the master is still a legitimate prescription, so the field never forces
 * a pick - it only offers one.
 */
export default function Autocomplete<T>({
  value, onChange, onPick, search, render, keyOf, placeholder, disabled, emptyNote,
}: Props<T>) {
  const [items, setItems] = useState<T[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [searched, setSearched] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  // What the user last typed. Only typing searches: a value written from
  // outside - a pick, a saved draft, a copied prescription - is already
  // settled, and searching it would pop a menu (or a "not in the list" note)
  // on every row the screen loads.
  const typed = useRef<string | null>(null)

  useEffect(() => {
    if (value !== typed.current) {
      setItems([])
      setOpen(false)
      setSearched(false)
      return
    }
    const term = value.trim()
    if (term.length < 2) {
      setItems([])
      setOpen(false)
      setSearched(false)
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      void search(term)
        .then((rows) => {
          if (cancelled) return
          setItems(rows)
          setActive(0)
          setSearched(true)
          setOpen(true)
        })
        .catch(() => {
          if (!cancelled) setItems([])
        })
    }, 160)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [value, search])

  useEffect(() => {
    function away(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  function choose(item: T) {
    typed.current = null
    onPick(item)
    setOpen(false)
    setItems([])
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!open || items.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (i + 1) % items.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (i - 1 + items.length) % items.length)
    } else if (e.key === 'Enter') {
      // Only claims Enter while a suggestion is highlighted, so typing a drug
      // that is not in the master and tabbing on still works.
      e.preventDefault()
      const item = items[active]
      if (item) choose(item)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="ac-wrap" ref={wrap}>
      <input
        value={value}
        onChange={(e) => { typed.current = e.target.value; onChange(e.target.value) }}
        onKeyDown={onKeyDown}
        onFocus={() => { if (items.length > 0) setOpen(true) }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <div className="ac-menu">
          {items.length === 0 && searched && emptyNote && (
            <div className="ac-empty">{emptyNote}</div>
          )}
          {items.map((item, i) => (
            <button
              key={keyOf(item)}
              type="button"
              className={`ac-item${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(item)}
            >
              {render(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
